import type { OperatorStatus } from '@/lib/api';

/**
 * Status badge for a <c>PlatformAccount</c>. Combines colour + icon + text so
 * the status is identifiable in monochrome mode (design D12 / WCAG 2.1 AA).
 *
 * For <c>Pending</c> accounts with a known <c>pendingExpiresAt</c>, the
 * badge surfaces the proximity of the expiration:
 * <list type="bullet">
 *   <item><c>diff &gt; 6h</c>: yellow "Pendente — expira em Nh"</item>
 *   <item><c>1h ≤ diff ≤ 6h</c>: yellow with explicit warning copy</item>
 *   <item><c>0 &lt; diff &lt; 1h</c>: red "expira em Nmin"</item>
 *   <item><c>diff ≤ 0</c>: grey "Convite expirado"</item>
 * </list>
 * The full ISO timestamp goes into the <c>title</c> attribute so the admin
 * can verify the exact moment without manual math.
 */
export function OperatorStatusBadge({
  status,
  pendingExpiresAt,
}: {
  status: OperatorStatus;
  pendingExpiresAt?: string | null;
}) {
  if (status === 'Pending' && pendingExpiresAt) {
    const expiresMs = new Date(pendingExpiresAt).getTime();
    // Ler o relogio no render e o proprio ponto deste badge: ele existe para
    // dizer quanto falta para o convite expirar. Congelar a leitura em estado
    // mostraria "expira em 3h" indefinidamente numa aba aberta. A granularidade
    // e de horas/minutos, e a instabilidade que a regra alerta nao muda o que o
    // operador ve.
    // eslint-disable-next-line react-hooks/purity
    const diffMs = expiresMs - Date.now();
    const tooltip = `Expira em ${formatAbsolute(pendingExpiresAt)}`;

    if (diffMs <= 0) {
      // Expirado: cinza (não-acionável até reenviar).
      return (
        <span
          className="badge"
          style={badgeStyle('#E5E7EB', '#374151', '#9CA3AF')}
          role="status"
          title={tooltip}
        >
          <span aria-hidden="true">⛔</span> Pendente (expirado)
        </span>
      );
    }

    if (diffMs < 3_600_000 /* < 1h */) {
      const minutes = Math.max(1, Math.ceil(diffMs / 60_000));
      return (
        <span
          className="badge"
          style={badgeStyle('#FEE2E2', '#7F1D1D', '#FCA5A5')}
          role="status"
          title={tooltip}
        >
          <span aria-hidden="true">⚠️</span> Pendente (expira em {minutes} min)
        </span>
      );
    }

    if (diffMs <= 21_600_000 /* ≤ 6h */) {
      const hours = Math.max(1, Math.ceil(diffMs / 3_600_000));
      return (
        <span
          className="badge"
          style={badgeStyle('#FEF3C7', '#78350F', '#FCD34D')}
          role="status"
          title={tooltip}
        >
          <span aria-hidden="true">⏳</span> Pendente (expira em {hours}h)
        </span>
      );
    }

    // Default Pending state: yellow, hours-resolution copy.
    const hoursLeft = Math.max(1, Math.floor(diffMs / 3_600_000));
    return (
      <span
        className="badge"
        style={badgeStyle('#FEF3C7', '#78350F', '#FCD34D')}
        role="status"
        title={tooltip}
      >
        <span aria-hidden="true">⏳</span> Pendente — expira em {hoursLeft}h
      </span>
    );
  }
  if (status === 'Active') {
    return (
      <span
        className="badge badge-success"
        style={badgeStyle('#D1FAE5', '#065F46', '#6EE7B7')}
        role="status"
      >
        <span aria-hidden="true">✓</span> Ativo
      </span>
    );
  }
  if (status === 'Revoked') {
    return (
      <span
        className="badge"
        style={badgeStyle('#E5E7EB', '#374151', '#9CA3AF')}
        role="status"
      >
        <span aria-hidden="true">✕</span> Revogado
      </span>
    );
  }
  // Fallback for Pending without expiry context.
  return (
    <span
      className="badge"
      style={badgeStyle('#FEF3C7', '#78350F', '#FCD34D')}
      role="status"
    >
      <span aria-hidden="true">⏳</span> Pendente
    </span>
  );
}

function badgeStyle(bg: string, fg: string, border: string): React.CSSProperties {
  return {
    background: bg,
    color: fg,
    border: `1px solid ${border}`,
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };
}

function formatAbsolute(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}
